import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Users, Pencil, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { MemberEditModal } from "./MemberEditModal";

export function FuncionariosTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/timeclock/members");
      setMembers(res.data);
    } catch {
      toast.error("Erro ao carregar funcionários");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4" data-testid="funcionarios-tab">
      <p className="text-sm text-muted-foreground">
        Clique num funcionário para editar os dados de contacto e as regras de notificação de ponto.
      </p>

      <div className="space-y-2">
        {members.map(m => <MemberCard key={m.id} member={m} onEdit={() => setEditing(m)} />)}

        {members.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Sem funcionários cadastrados</p>
              <p className="text-xs mt-1">Adicione membros na aba "Equipa" primeiro</p>
            </CardContent>
          </Card>
        )}
      </div>

      {editing && (
        <MemberEditModal
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={fetchMembers}
        />
      )}
    </div>
  );
}

function MemberCard({ member: m, onEdit }) {
  const ns = m.notification_settings || {};
  const phone = (m.contact?.whatsapp || m.contact?.phone || m.phone || "");
  const email = (m.contact?.email || m.email || "");
  const hasWa = !!phone && ns.whatsapp_enabled;
  const hasEmail = !!email && ns.email_enabled;

  return (
    <Card
      className="border-border/50 cursor-pointer hover:border-emerald-400 hover:shadow-sm transition-all"
      onClick={onEdit}
      data-testid={`member-card-${m.id?.slice(-6)}`}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <span className="text-sm font-bold text-emerald-700">{m.name?.charAt(0)?.toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm truncate">{m.name}</p>
            {m.active === false && (
              <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">Inativo</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground capitalize">{m.role || "—"}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {phone && (
              <Badge active={hasWa} activeClass="bg-green-100 text-green-700" label="WA" />
            )}
            {email && (
              <Badge active={hasEmail} activeClass="bg-blue-100 text-blue-700" label="Email" />
            )}
            {ns.before_shift_enabled && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                -{ns.before_shift_minutes || 5}min
              </span>
            )}
            {ns.missing_clockin_enabled && (
              <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">
                +{ns.missing_clockin_minutes || 10}min alerta
              </span>
            )}
          </div>
        </div>
        <Pencil className="w-4 h-4 text-muted-foreground shrink-0" />
      </CardContent>
    </Card>
  );
}

function Badge({ active, activeClass, label }) {
  return (
    <span className={`text-[10px] flex items-center gap-0.5 px-1.5 py-0.5 rounded font-medium ${active ? activeClass : "bg-muted text-muted-foreground"}`}>
      {label} {active ? <CheckCircle className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
    </span>
  );
}
